import React from 'react';
import { getBackendUrl } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';

const Footer = ({ isAuthenticated, user }) => {
    const currentYear = new Date().getFullYear();
    const backendUrl = getBackendUrl();
    const { t } = useLanguage();

    return (
        <footer className="bg-gradient-to-r from-[#1A0730] via-[#2D0C4B] to-[#1A0730] text-white py-8 mt-auto">
            <div className="max-w-7xl mx-auto px-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
                    {/* Company Info */}
                    <div>
                        <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                            <span className="text-2xl">📻</span> 
                            <span className="bg-gradient-to-r from-[#8A4FDB] to-[#FF9F0A] bg-clip-text text-transparent">Radio Solutions</span>
                        </h3>
                        <p className="text-white/70 text-sm">
                            {t('radioContentCreation')}
                        </p>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h3 className="text-lg font-bold mb-3 text-[#FF9F0A]">{t('quickLinks')}</h3>
                        <ul className="space-y-2 text-sm">
                            {/* Protected Links - Visible only when authenticated */}
                            {isAuthenticated && (
                                <>
                                    <li>
                                        <a href="/on-air-content" className="text-white/80 hover:text-[#FF9F0A] transition-colors">
                                            ✨ {t('onAirContent') || 'On Air Content'}
                                        </a>
                                    </li>
                                    <li>
                                        <a href="/song-info" className="text-white/80 hover:text-[#FF9F0A] transition-colors">
                                            🎵 {t('songInfo') || 'Πληροφορίες Τραγουδιών'}
                                        </a>
                                    </li>
                                    <li>
                                        <a href="/metritis-kampanias" className="text-white/80 hover:text-[#FF9F0A] transition-colors">
                                            📊 {t('campaignAnalyzer')}
                                        </a>
                                    </li>
                                    <li>
                                        <a href="/metritis-akroaton" className="text-white/80 hover:text-[#FF9F0A] transition-colors">
                                            📊 {t('listenerCalculator')}
                                        </a>
                                    </li>
                                    <li>
                                        <a href="/radio-top-songs" className="text-white/80 hover:text-[#FF9F0A] transition-colors">
                                            📻 {t('radioTopSongs')}
                                        </a>
                                    </li>
                                    {/* v2.13.53.1 — Removed Playlist Analyzer and
                                        Daypart Analyzer footer links (pages deleted). */}
                                    {/* Songs Analyzer PRO - For subscribers/admin */}
                                    <li>
                                        <a 
                                            href="/songs-analyzer"
                                            className="text-white/80 hover:text-[#FF9F0A] transition-colors inline-flex items-center gap-2"
                                        >
                                            📈 Songs Analyzer
                                            <span className="text-[9px] bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-1.5 py-0.5 rounded-full font-bold">PRO</span>
                                        </a>
                                    </li>
                                    {/* The People's Chart - Only for subscribers/admin */}
                                    <li>
                                        <a 
                                            href="/radio-chart"
                                            className="text-white/80 hover:text-[#FF9F0A] transition-colors inline-flex items-center gap-2"
                                        >
                                            📊 The People's Chart
                                            <span className="text-[9px] bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-1.5 py-0.5 rounded-full font-bold">PRO</span>
                                        </a>
                                    </li>
                                    
                                    {/* Compare Stations - Only for Playlister/Full Package/admin */}
                                    <li>
                                        <a 
                                            href="/compare-stations"
                                            className="text-white/80 hover:text-[#FF9F0A] transition-colors inline-flex items-center gap-2"
                                        >
                                            🔀 Compare Stations
                                            <span className="text-[9px] bg-gradient-to-r from-purple-500 to-teal-500 text-white px-1.5 py-0.5 rounded-full font-bold">PRO</span>
                                        </a>
                                    </li>
                                </>
                            )}
                            {/* Public Links - Always visible */}
                            <li>
                                <a 
                                    href="/our-stations"
                                    className="text-white/80 hover:text-[#FF9F0A] transition-colors"
                                >
                                    🎙️ {t('ourStations')}
                                </a>
                            </li>
                            <li>
                                <a 
                                    href="/about"
                                    className="text-white/80 hover:text-[#FF9F0A] transition-colors"
                                >
                                    ℹ️ {t('aboutUs')}
                                </a>
                            </li>
                            <li>
                                <a 
                                    href="/contact"
                                    className="text-white/80 hover:text-[#FF9F0A] transition-colors"
                                >
                                    📧 {t('contact')}
                                </a>
                            </li>
                            {/* Pricing Link */}
                            <li>
                                <a 
                                    href="/pricing"
                                    className="text-white/80 hover:text-[#FF9F0A] transition-colors"
                                >
                                    💎 {t('pricing')}
                                </a>
                            </li>
                            {/* Message for non-authenticated users */}
                            {!isAuthenticated && (
                                <li className="text-white/50 text-xs italic pt-2">
                                    🔒 {t('loginForMoreOptions')}
                                </li>
                            )}
                        </ul>
                    </div>

                    {/* Legal & GDPR */}
                    <div>
                        <h3 className="text-lg font-bold mb-3 text-[#FF9F0A]">{t('legalPrivacy')}</h3>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <a 
                                    href={`${backendUrl}/privacy-policy.html`}
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-white/80 hover:text-[#FF9F0A] transition-colors flex items-center gap-1"
                                >
                                    🔒 {t('privacyPolicy')}
                                </a>
                            </li>
                            <li>
                                <a 
                                    href={`${backendUrl}/terms-of-service.html`}
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-white/80 hover:text-[#FF9F0A] transition-colors flex items-center gap-1"
                                >
                                    📜 {t('termsOfService')}
                                </a>
                            </li>
                            <li>
                                <a 
                                    href={`${backendUrl}/cookie-policy.html`}
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-white/80 hover:text-[#FF9F0A] transition-colors flex items-center gap-1"
                                >
                                    🍪 {t('cookiePolicy')}
                                </a>
                            </li>
                            <li>
                                <button 
                                    onClick={() => {
                                        // Delete cookie consent to show banner again
                                        document.cookie = 'cookie_consent=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
                                        window.location.reload();
                                    }}
                                    className="text-white/80 hover:text-[#FF9F0A] transition-colors flex items-center gap-1"
                                >
                                    ⚙️ {t('cookieSettings')}
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="border-t border-white/20 pt-6">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-white/70 text-sm">
                            © {currentYear} Radio Solutions. {t('allRightsReserved')}.
                        </p>
                        <div className="flex items-center gap-4 text-sm text-white/70">
                            <span className="flex items-center gap-1">
                                🔒 {t('gdprCompliant')}
                            </span>
                            <span className="flex items-center gap-1">
                                🇪🇺 {t('euDataProtection')}
                            </span>
                        </div>
                    </div>
                    <p className="text-center text-xs text-white/50 mt-4">
                        {t('gdprNotice')}
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
